import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import AppointmentCancellationConfirm from '~appointments/AppointmentCancellationConfirm';
import classNames from 'classnames';
import dayjs from 'dayjs';

import { AuthTags } from '~constants/authTags';
import { PackageScheduleFundingTypes } from '~constants/maps';

import { dateInputFieldFormat } from '~libs/dayjs';

import { ButtonProps } from '~components';
import { FieldProps } from '~components/form-fields';
import Icon, { faArrowLeft } from '~components/icon';
import Spinner from '~components/Spinner';
import { AppointmentBanner, AppointmentForm } from '~appointments/components';

import { appointmentFields, repeatFields } from '~appointments/forms/appointment/cancellation';
import { appointmentEditFields, repeatAppointmentEditFields } from '~appointments/forms/appointment/edit';

import { getAll as getActivities } from '~appointments/actions/activities';
import {
  cancel as cancelAppointment,
  getOne as getAppointment,
  getAll as getAppointments,
} from '~appointments/actions/appointments';
import { getAll } from '~appointments/actions/cancellationReasons';
import { getAll as getEquipments } from '~lookups/actions/equipments';
import { getAll as getMedications } from '~lookups/actions/medications';
import { getAll as getTaskClassifications } from '~lookups/actions/taskClassifications';
import { getAll as getTaskTypes } from '~lookups/actions/taskTypes';
import { setTitle } from '~main/actions/login';

import { clearAppointment } from '~appointments/reducers/appointments';

import {
  selectAppointmentLoading,
  selectAppointments,
  selectCurrentAppointment,
} from '~appointments/selectors/appointments';
import { selectCompanySettings } from '~main/selectors/login';

const AppointmentCancellation: React.FC<any> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const authTag = AuthTags.APPOINTMENTS.CANCEL;

  const [changedValues, setChangedValues]: [any, any] = useState({});
  const [confirmOpen, setConfirmOpen]: [boolean, any] = useState(false);

  // Link record from selector to component
  const appointment = useAppSelector(selectCurrentAppointment);
  const linkedAppointments = useAppSelector(selectAppointments);
  const appointmentLoading = useAppSelector(selectAppointmentLoading);
  const settings = useAppSelector(selectCompanySettings);
  const defaultNotifyCareWorker = settings?.SYSTEM_CANCELLATION_DEFAULT_NOTIFY_CARE_WORKER === true;

  const isRepeat = appointment?.repeat;

  let cancellationFields: FieldProps;
  if (isRepeat) cancellationFields = repeatFields;
  else cancellationFields = appointmentFields;

  let apptEditFields: FieldProps;
  if (isRepeat) apptEditFields = repeatAppointmentEditFields;
  else apptEditFields = appointmentEditFields;

  useEffect(() => {
    setTitle(`Cancel appt`);
    // Get appointment by ID on mount if not current appointment
    dispatch(getAppointment({ id }));
    dispatch(getActivities({ parent_id: id, sort: ['date_time'] }));

    dispatch(
      getAppointments({
        repeat: false,
        date: { gte: dayjs().format(dateInputFieldFormat) },
        parent_repeat_id: id,
        sort: ['date', 'start_time'],
      }),
    );

    // Get task classifications and types
    dispatch(getTaskClassifications());
    dispatch(getTaskTypes({ disabled: false, sort: ['classification', 'name'] }));

    // Get medications
    dispatch(getMedications({ disabled: false, sort: ['name'] }));

    // Get equipments
    dispatch(getEquipments({ disabled: false, sort: ['name'] }));

    // Clean up component on unmount, clear reducer, etc.
    return () => {
      dispatch(clearAppointment());
    };
  }, []);

  useEffect(() => {
    // Get cancellation reasons based on appointment funding type
    if (appointment) {
      setTitle(`Cancel #${appointment?.reference_number ?? ''}`);
      const { package_fixed } = appointment;
      dispatch(getAll({ include_ndis: package_fixed?.funding_type_id === PackageScheduleFundingTypes.NDIS }));
    }
  }, [appointment]);

  const save = async (values: any) => {
    // Always ensure chargeable_cancellation is present, defaulting to false if not set
    if (typeof values.chargeable_cancellation === 'undefined') {
      values.chargeable_cancellation = false;
    }
    // Always include notify_care_worker — getChangedValues strips it when the toggle
    // is unchanged from the company default, causing the API to silently skip notification.
    if (typeof values.notify_care_worker === 'undefined') {
      values.notify_care_worker = defaultNotifyCareWorker;
    }
    const { payload } = await dispatch(
      cancelAppointment({
        id,
        ...values,
      }),
    );
    if (payload?.success) navigate(`/appointments/${id}`);
  };

  const onSave = (values: any) => {
    const confirmationRequired = isRepeat && linkedAppointments.length > 0;
    if (!confirmationRequired) save(values);
    else {
      setChangedValues(values);
      setConfirmOpen(true);
    }
  };

  const onConfirm = (confirmed: boolean, ids: string[], values: any) => {
    setConfirmOpen(false);

    if (confirmed) {
      save({
        ...values,
        including_children: ids,
      });
    }
  };

  const isLoading = appointmentLoading === 'pending';

  const allFields = Object.entries(apptEditFields).map(([key, props]) => ({
    key,
    disabled: true,
    ...props,
  }));

  const confirmButtonProps: ButtonProps = {
    authTag,
    dataCy: 'appointment-cancellation-confirm-button',
    color: 'danger',
    className: 'm-3',
    children: 'Confirm',
  };

  const backButtonProps: ButtonProps = {
    authTag,
    color: 'success',
    dataCy: 'appointment-cancellation-back-button',
    className: 'm-3',
    onClick: () => {
      appointment && navigate(`/appointments/${appointment.id}`);
    },
    children: (
      <>
        <Icon className="me-2" icon={faArrowLeft} />
        <span>Back</span>
      </>
    ),
  };

  const getBannerProps = () => {
    // Header class name determination
    const headerClassName = classNames({
      warning: isRepeat,
      info: !isRepeat,
      'text-white': isRepeat,
    });

    // Header text determination
    const headerText = `Appointment Details - Ref #${appointment?.reference_number}`;

    // Data-cy attribute determination
    const dataCy = isRepeat ? 'repeat-appointments-header' : 'appointment-header';

    // Icon color determination
    const iconColor = isRepeat ? 'white' : undefined;

    return {
      headerClassName,
      headerText,
      dataCy,
      iconColor,
      showRepeatLabel: isRepeat,
      showCancellationMessage: true,
      isNew: false,
      onBackNavigatesTo: isRepeat ? 'repeats' : 'appointments',
    };
  };

  return (
    <div className="p-2">
      {!isLoading && (
        <>
          <AppointmentCancellationConfirm
            isOpen={confirmOpen}
            rows={linkedAppointments}
            onClose={(confirm, ids) => onConfirm(confirm, ids, changedValues)}
          />
          <AppointmentBanner {...getBannerProps()} />
          <div className="border border-2 rounded border-danger">
            <AppointmentForm
              authTag={authTag}
              fields={cancellationFields}
              canSave={() => true}
              canCancel={() => true}
              save={onSave}
              confirmButtonProps={confirmButtonProps}
              cancelButtonProps={backButtonProps}
              data-cy={'appointment-cancellation-form'}
              defaultValues={{ chargeable_cancellation: false, notify_care_worker: defaultNotifyCareWorker }}
            />
          </div>
          <div className="mt-2">
            <AppointmentForm
              authTag={authTag}
              fields={allFields}
              showActivityLogs={true}
              data-cy={'appointment-edit-form'}
            />
          </div>
        </>
      )}
      {isLoading && <Spinner loading={isLoading} className="mt-5" />}
    </div>
  );
};

export default AppointmentCancellation;
